/**
 * Validation utilities for car hire approval flow.
 * Determines when an approval is invalid and when to clear it.
 */

export type CarHireApprovalInvalidReason =
  | 'offer'
  | 'location_dates'
  | 'budget'
  | undefined

export function getCarHireApprovalInvalidReason(params: {
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined
  approvalPolicy: App.BusinessTraveller.ApprovalPolicy | null
  carHireViews: Array<App.Checkout.CarHireItemView>
  carHireTotal: number
}): CarHireApprovalInvalidReason {
  const { approvalRequest, approvalPolicy, carHireViews, carHireTotal } = params

  if (!approvalRequest || !approvalPolicy || carHireViews.length === 0) {
    return undefined
  }

  const carHireApprovalItem = approvalRequest.tripRequestItems?.find(
    (item): item is App.BusinessTraveller.CarHireTripRequestItem => item.type === 'car_hire',
  )

  if (!carHireApprovalItem) {
    return undefined
  }

  const carHireView = carHireViews[0]
  const item = carHireView.item

  // Check offer match (different car selected)
  const approvedOfferId = carHireApprovalItem.displayData?.carHireDetails?.offerId
  if (approvedOfferId && item.offerId !== approvedOfferId) {
    return 'offer'
  }

  // Check location/dates match
  const approvalDetails = carHireApprovalItem.displayData?.carHireDetails
  const displayData = carHireApprovalItem.displayData
  const pickUpIdMismatch =
    approvalDetails?.pickUpId && item.pickUpLocationId !== approvalDetails.pickUpId
  const dropOffIdMismatch =
    approvalDetails?.dropOffId && item.dropOffLocationId !== approvalDetails.dropOffId
  const pickupDateMismatch =
    displayData?.pickupDate && item.pickUpDate !== displayData.pickupDate
  const dropoffDateMismatch =
    displayData?.dropoffDate && item.dropOffDate !== displayData.dropoffDate

  if (pickUpIdMismatch || dropOffIdMismatch || pickupDateMismatch || dropoffDateMismatch) {
    return 'location_dates'
  }

  // Check budget (price exceeded approved total + buffer)
  const approvalLimit = approvalRequest.total ?? 0
  if (approvalLimit > 0) {
    const priceBufferPercent = approvalPolicy?.priceBuffer ?? 0
    const maxAllowed = approvalLimit + (approvalLimit * priceBufferPercent) / 100
    if (carHireTotal > maxAllowed) {
      return 'budget'
    }
  }

  return undefined
}

export function isCarHireOutOfBudget(
  carHireTotal: number,
  carHireBudgetTotal: number,
  treatMissingBudgetAsOutOfBudget: boolean = false,
): boolean {
  if (treatMissingBudgetAsOutOfBudget && carHireBudgetTotal <= 0) {
    return true
  }
  return carHireBudgetTotal > 0 && carHireTotal > carHireBudgetTotal
}

/**
 * When policy scope is NEVER and user has invalid approval (e.g. over budget),
 * clear approval so they proceed to payment without blocking.
 */
export function shouldClearApprovalForNeverScopeWhenOverBudget(
  approvalPolicy: App.BusinessTraveller.ApprovalPolicy | null,
  invalidReason: CarHireApprovalInvalidReason,
  totalWithoutServiceFee: number,
  companyBudget: number,
): boolean {
  if (!approvalPolicy || approvalPolicy.scope !== 'NEVER' || !invalidReason) {
    return false
  }
  if (invalidReason === 'budget' && companyBudget > 0 && totalWithoutServiceFee > companyBudget) {
    return true
  }
  return false
}
